// Group by (Leon, 2026-10-23): rows fold under a heading per value, each with
// its count and the subtotals of the money columns — the pivot-table answer
// without a pivot table. Returns the lines the grid draws, in order, and the
// rows on screen that selection indexes into. Pure, so tested.

import { cellText, type SheetRow, sumOf } from "./sheet-table.model";

export type SheetLine<T> =
  | {
      kind: "group";
      value: string;
      label: string;
      count: number;
      sums: Record<string, number>;
      collapsed: boolean;
    }
  | { kind: "row"; row: T; index: number };

export interface GroupedRows<T> {
  lines: SheetLine<T>[];
  /** The rows on screen, in order — what a selection's row index points at. */
  rows: T[];
}

export function groupRows<T extends SheetRow>(
  rows: readonly T[],
  groupBy: string | null,
  sumKeys: readonly string[],
  collapsed: ReadonlySet<string>,
  labelOf: (value: string) => string = (value) => value,
): GroupedRows<T> {
  if (!groupBy) {
    return { lines: rows.map((row, index) => ({ kind: "row", row, index })), rows: [...rows] };
  }
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const value = cellText(row[groupBy]);
    const members = groups.get(value);
    if (members) members.push(row);
    else groups.set(value, [row]);
  }
  const order = [...groups.keys()].sort((a, b) =>
    a === ""
      ? 1
      : b === ""
        ? -1
        : labelOf(a).localeCompare(labelOf(b), undefined, { numeric: true }),
  );
  const lines: SheetLine<T>[] = [];
  const shown: T[] = [];
  for (const value of order) {
    const members = groups.get(value) ?? [];
    const isCollapsed = collapsed.has(value);
    lines.push({
      kind: "group",
      value,
      label: value === "" ? "(Blank)" : labelOf(value),
      count: members.length,
      sums: Object.fromEntries(sumKeys.map((key) => [key, sumOf(members, key)])),
      collapsed: isCollapsed,
    });
    if (isCollapsed) continue;
    for (const row of members) {
      lines.push({ kind: "row", row, index: shown.length });
      shown.push(row);
    }
  }
  return { lines, rows: shown };
}
